import { Calendar, Clock, MapPin, Users, DollarSign, Dribbble } from "lucide-react";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { PageHeader } from "@/components/PageHeader";
import { Reveal } from "@/components/Reveal";
import { Button } from "@/components/ui/button";
import { InterestDialog } from "@/components/dialogs/InterestDialog";
import { ASSETS } from "@/lib/constants";
import { useSEO } from "@/hooks/useSEO";

const DETAILS = [
  { icon: Calendar, label: "Dates", value: "Summer — TBA" },
  { icon: Clock, label: "Times", value: "TBA" },
  { icon: MapPin, label: "Location", value: "Norwalk, CT — TBA" },
  { icon: Users, label: "Age Groups", value: "All ages 10U–17U" },
  { icon: DollarSign, label: "Price", value: "TBA" },
  { icon: Dribbble, label: "Skill Level", value: "All levels welcome" },
];

const WORK_ON = [
  "Ball-handling & dribbling moves",
  "Shooting form & finishing",
  "Footwork & defense",
  "Passing & decision-making",
  "Conditioning & competitive scrimmages",
  "Confidence & basketball IQ",
];

export default function SummerClinics() {
  useSEO({
    title: "Summer Basketball Clinics in Norwalk, CT | CT BLAZERS — All Skill Levels",
    description:
      "CT BLAZERS summer basketball clinics in Norwalk, CT. All skill levels welcome. Develop skills, confidence and understanding of the game. Register your interest.",
    keywords: "Youth Basketball Norwalk CT, summer basketball clinics Connecticut, CT Blazers clinics",
    path: "/summer-clinics",
  });

  return (
    <div className="bg-ink-900 min-h-screen">
      <Navbar />
      <PageHeader
        eyebrow="All Skill Levels Welcome"
        title="Summer"
        highlight="Clinics"
        image={ASSETS.clinicHuddle}
        subtitle="Summer basketball clinics designed to help players develop their skills, confidence, and understanding of the game — in a fun, competitive, family-friendly environment."
      >
        <InterestDialog category="clinic" program="Summer Clinics" testId="clinic">
          <Button
            data-testid="clinic-register-btn"
            className="bg-blaze-orange hover:bg-blaze-orangeBright text-ink-900 font-bold uppercase tracking-wide rounded-full h-12 px-8 shadow-[0_10px_30px_rgba(246,140,31,0.4)] transition-all hover:-translate-y-1"
          >
            Register For Clinics
          </Button>
        </InterestDialog>
      </PageHeader>

      <section className="py-16 sm:py-24 bg-ink-800">
        <div className="container-x">
          <Reveal>
            <div className="inline-flex items-center gap-2 rounded-full border border-blaze-orange/40 bg-blaze-orange/10 px-5 py-2 text-sm font-bold uppercase tracking-[0.2em] text-blaze-orange">
              All Skill Levels Welcome
            </div>
          </Reveal>

          <div className="mt-8 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {DETAILS.map((d, i) => (
              <Reveal key={d.label} delay={(i % 3) * 0.08}>
                <div className="rounded-2xl border border-white/10 bg-ink-700 p-6 h-full">
                  <div className="grid h-11 w-11 place-items-center rounded-lg bg-blaze-blue/15 text-blaze-blueLight">
                    <d.icon size={22} />
                  </div>
                  <p className="mt-4 text-xs uppercase tracking-wider text-white/50">{d.label}</p>
                  <p className="mt-1 font-heading text-2xl tracking-wide text-white">{d.value}</p>
                </div>
              </Reveal>
            ))}
          </div>

          <div className="mt-10 grid gap-6 lg:grid-cols-2">
            <Reveal>
              <div className="rounded-2xl border border-white/10 bg-ink-700/60 p-8 h-full">
                <h2 className="font-display font-black uppercase tracking-tight text-3xl text-white">What Players Work On</h2>
                <div className="mt-6 grid sm:grid-cols-2 gap-3">
                  {WORK_ON.map((w) => (
                    <div key={w} className="flex items-start gap-3 rounded-lg border border-white/10 bg-ink-700/60 px-4 py-3">
                      <span className="mt-1.5 h-2 w-2 shrink-0 rounded-full bg-blaze-orange" />
                      <span className="text-sm text-white/75">{w}</span>
                    </div>
                  ))}
                </div>
              </div>
            </Reveal>
            <Reveal delay={0.1}>
              <div className="rounded-2xl border border-white/10 bg-gradient-to-br from-blaze-blue/20 to-ink-700 p-8 h-full flex flex-col justify-between">
                <div>
                  <h3 className="font-heading text-3xl tracking-wide text-white">Ready To Register?</h3>
                  <p className="mt-3 text-white/70 leading-relaxed">
                    Spots are limited. Register your interest and we'll send you clinic dates, times, location and pricing
                    as soon as they're confirmed.
                  </p>
                </div>
                <InterestDialog category="clinic" program="Summer Clinics" testId="clinic-bottom">
                  <Button
                    data-testid="clinic-register-btn-2"
                    className="mt-6 bg-blaze-orange hover:bg-blaze-orangeBright text-ink-900 font-bold uppercase tracking-wide rounded-full h-12"
                  >
                    Register For Clinics
                  </Button>
                </InterestDialog>
              </div>
            </Reveal>
          </div>
        </div>
      </section>
      <Footer />
    </div>
  );
}
